import React, { useEffect, useState } from 'react';
import ReactDOM from 'react-dom';
import { UserRole, User, UserCreateRequest, UserUpdateRequest } from '../../types/user.type';
import { Button } from '../../components/Button';
import { useDispatch } from 'react-redux';
import { AppDispatch } from '../../state/store';
import { createUser, updateUser } from '../../services/UserService';
import { showSuccessToast, showErrorToast } from '../../lib/ToastUtility';

interface UserFormModalProps {
  open: boolean;
  onClose: () => void;
  user?: User; // If provided, we're editing; if not, we're creating
  onSuccess?: () => void; // Callback for when the operation is successful
}

const UserFormModal: React.FC<UserFormModalProps> = ({
  open,
  onClose,
  user,
  onSuccess
}) => {
  const isEditMode = !!user;
  const dispatch = useDispatch<AppDispatch>();

  const [formData, setFormData] = useState<UserCreateRequest | UserUpdateRequest>({
    email: '',
    name: '',
    contactNumber: '',
    role: UserRole.DONOR,
    language: 'en',
    active: true
  });

  const [errors, setErrors] = useState<Record<string, string>>({});

  useEffect(() => {
    if (user) {
      setFormData({
        id: user.id,
        email: user.email,
        name: user.name,
        contactNumber: user.contactNumber || '',
        role: user.role as UserRole,
        language: user.language,
        active: user.active
      });
    } else {
      // Reset form for create mode
      setFormData({
        email: '',
        name: '',
        contactNumber: '',
        role: UserRole.DONOR,
        language: 'en',
        active: true
      });
    }
    // Clear errors when modal opens/closes or user changes
    setErrors({});
  }, [user, open]);

  if (!open) return null;

  const validateForm = (): boolean => {
    const newErrors: Record<string, string> = {};
    
    if (!formData.email) {
      newErrors.email = 'Email is required';
    } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
      newErrors.email = 'Email is invalid';
    }
    
    if (!formData.name) {
      newErrors.name = 'Name is required';
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async () => {
    if (!validateForm()) return;
    
    // Ensure no null values are sent to the backend
    const sanitizedFormData = {
      ...formData,
      email: formData.email || '',
      name: formData.name || '',
      contactNumber: formData.contactNumber || '',
      language: formData.language || 'en',
    };
    
    try {
      if (isEditMode) {
        await dispatch(updateUser(sanitizedFormData as UserUpdateRequest)).unwrap();
        showSuccessToast('User updated successfully');
      } else {
        await dispatch(createUser(sanitizedFormData as UserCreateRequest)).unwrap();
        showSuccessToast('User created successfully');
      }
      
      onClose();
      if (onSuccess) {
        onSuccess();
      }
    } catch (error) {
      showErrorToast((error as Error).message || 'An error occurred');
    }
  };

  return ReactDOM.createPortal(
    <div className="fixed inset-0 bg-black bg-opacity-50 z-[9999] flex items-center justify-center p-4">
      <div className="bg-white dark:bg-slate-900 rounded-2xl shadow-xl border border-white/50 dark:border-white/10 w-full max-w-xl p-6">
        <h2 className="text-xl font-semibold text-gray-900 dark:text-gray-100 mb-4">
          {isEditMode ? 'Edit User' : 'Create New User'}
        </h2>
        
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Email*</label>
            <input
              type="email"
              placeholder="Email"
              value={formData.email || ''}
              onChange={(e) => setFormData({ ...formData, email: e.target.value })}
              className="w-full border border-gray-300 dark:border-white/10 dark:bg-slate-800 dark:text-gray-100 rounded-md p-2 focus:ring-primary-100 focus:border-primary-100"
            />
            {errors.email && <p className="text-red-500 text-sm mt-1">{errors.email}</p>}
          </div>
          
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Name*</label>
            <input
              type="text"
              placeholder="Full Name"
              value={formData.name || ''}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              className="w-full border border-gray-300 dark:border-white/10 dark:bg-slate-800 dark:text-gray-100 rounded-md p-2 focus:ring-primary-100 focus:border-primary-100"
            />
            {errors.name && <p className="text-red-500 text-sm mt-1">{errors.name}</p>}
          </div>
          
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Contact Number</label>
            <input
              type="text"
              placeholder="Phone Number"
              value={formData.contactNumber || ''}
              onChange={(e) => setFormData({ ...formData, contactNumber: e.target.value })}
              className="w-full border border-gray-300 dark:border-white/10 dark:bg-slate-800 dark:text-gray-100 rounded-md p-2 focus:ring-primary-100 focus:border-primary-100"
            />
          </div>
          
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Role*</label>
            <select
              className="border border-gray-300 dark:border-white/10 dark:bg-slate-800 dark:text-gray-100 rounded-md w-full p-2 focus:ring-primary-100 focus:border-primary-100"
              value={formData.role}
              onChange={(e) => setFormData({ 
                ...formData, 
                role: e.target.value as UserRole 
              })}
            >
              <option value={UserRole.SYSTEM}>System</option>
              <option value={UserRole.DONOR}>Donor</option>
            </select>
          </div>
          
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Language</label>
            <select
              className="border border-gray-300 dark:border-white/10 dark:bg-slate-800 dark:text-gray-100 rounded-md w-full p-2 focus:ring-primary-100 focus:border-primary-100"
              value={formData.language}
              onChange={(e) => setFormData({ ...formData, language: e.target.value })}
            >
              <option value="en">English</option>
              <option value="fr">French</option>
              <option value="es">Spanish</option>
            </select>
          </div>
          
          {!isEditMode && (
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Password</label>
              <input
                type="password"
                placeholder="Password"
                value={(formData as UserCreateRequest).password || ''}
                onChange={(e) => {
                  if (isEditMode) {
                    return; // Should not happen due to conditional rendering
                  } else {
                    // Only add password for UserCreateRequest
                    setFormData({
                      ...formData as UserCreateRequest,
                      password: e.target.value
                    } as UserCreateRequest);
                  }
                }}
                className="w-full border border-gray-300 dark:border-white/10 dark:bg-slate-800 dark:text-gray-100 rounded-md p-2 focus:ring-primary-100 focus:border-primary-100"
              />
              <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                Leave empty to generate a random password
              </p>
            </div>
          )}
          
          <div className="flex items-center">
            <input 
              type="checkbox"
              className="p-2 text-lg font-thin rounded-md border-2 border-gray-300 dark:border-white/10 dark:bg-slate-800"
              checked={formData.active === true}
              onChange={(e) => setFormData({ ...formData, active: e.target.checked })}
            />
            <span className="ml-2 dark:text-gray-300">Active</span>
          </div>
        </div>
        
        <div className="flex justify-end space-x-2 mt-6">
          <Button
            variant="secondary"
            size="md"
            onClick={onClose}
          >
            Cancel
          </Button>
          <Button
            variant="primary"
            size="md"
            onClick={handleSubmit}
          >
            {isEditMode ? 'Update User' : 'Create User'}
          </Button>
        </div>
      </div>
    </div>
  ,
  document.body
);
};

export default UserFormModal;